import { date as fmtDate } from '@orgazone/date-format';
import { useOverlayPanel } from '@orgazone/ui-web';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, hhmmss, rawDownloadUrl, statusKind, statusLabel, type ApiMessage, type ApiMessageDetail } from '../api';
import { Icon } from '../components/icons';
import { Badge, Btn, Card, DataGate, PageHead, type BadgeKind } from '../components/ui';
import { isInTransit, reachedStep } from '../message-progress';
import { useApp } from '../state';
import { useResource } from '../useResource';

const PER = 12;
/** Nombre de messages lus par la route — cf. le commentaire sur `useResource` : l'écran l'ANNONCE. */
const PLAFOND_LECTURE = 500;
type SortKey = 'time' | 'dir' | 'supplier' | 'site' | 'type' | 'status' | 'replays';

// Ligne d'affichage dérivée d'un message réel (l'API renvoie les champs bruts + libellés joints).
interface Row {
  id: string; time: string; iso: string; dir: 'in' | 'out'; supplier: string; gln: string; site: string;
  type: string; transport: string; kind: BadgeKind; status: string; statusRaw: string; replays: number;
}
function toRow(m: ApiMessage): Row {
  return {
    id: m.id, iso: m.createdAt, time: hhmmss(m.createdAt), dir: m.direction,
    supplier: m.supplierName ?? '—', gln: m.gln ?? '—', site: m.siteName ?? (m.gln ? '—' : 'non résolu'),
    type: m.canonicalType ?? '—', transport: m.transportType ?? '—',
    kind: statusKind(m.status), status: statusLabel(m.status), statusRaw: m.status, replays: m.replayCount,
  };
}

// Largeur de fenêtre → volet latéral (≥1180) vs superposition plein écran (<1180). HANDOFF §3.
function useNarrow(bp = 1180) {
  const [n, setN] = useState(typeof window !== 'undefined' && window.innerWidth < bp);
  useEffect(() => {
    const on = () => setN(window.innerWidth < bp);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [bp]);
  return n;
}

export function Journal() {
  const { showToast, guard } = useApp();
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';

  // Journal VIDE = aucun message reçu ni déposé : un état réel, qui mérite son écran dédié (le
  // « aucun message pour ces filtres » de la table dirait autre chose).
  /**
   * ⚠️ 🔴 LE PLAFOND EST NOMMÉ PARCE QUE L'ÉCRAN DOIT LE DIRE. La lecture s'arrête aux N messages les plus
   * récents, et rien ne l'annonçait : au-delà, le journal présentait une vue TRONQUÉE comme si elle était
   * complète — « aucun échange ne correspond » pouvait donc vouloir dire « pas sur les 500 derniers »
   * (§23.0). Le chiffre vit ICI, à un seul endroit, pour que la phrase ne puisse pas mentir sur la requête.
   */
  const { state, data, reload, error } = useResource((signal) => api.get<ApiMessage[]>(`/admin/messages?limit=${PLAFOND_LECTURE}`, signal));
  // ⚠️ Repli mémoïsé et non `data ?? []` : les trois mémos de filtrage/tri dépendent de cette liste,
  // et une identité neuve à chaque rendu les rendrait inopérants.
  const raw = useMemo(() => data ?? [], [data]);

  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [dir, setDir] = useState<'all' | 'in' | 'out'>('all');
  const [sup, setSup] = useState('all');
  const [site, setSite] = useState('all');
  const [page, setPage] = useState(0);
  const [sel, setSel] = useState<string | null>(null);
  /**
   * Statuts que le VOLET a relus depuis le chargement de la liste.
   *
   * ⚠️ 🔴 POURQUOI LA LISTE DOIT LES RECEVOIR : le volet suit son message en direct, la liste est une photo
   * prise une fois. Les deux sont affichés DANS LA MÊME FENÊTRE (volet latéral ≥1180) — sans ce report, le
   * tableau annonçait « En erreur » à vingt centimètres d'un volet disant « Confirmé », pour le même
   * message. Un écran qui se contredit lui-même est pire qu'un écran en retard (§23.0).
   * ⚠️ PAS un rechargement de la liste : `reload()` vide l'écran le temps de la requête (et démonte le
   * volet), donc l'appeler à chaque bascule ferait clignoter le journal trois fois par rejeu.
   */
  const [observed, setObserved] = useState<Record<string, string>>({});
  /**
   * ⚠️ 🔴 IDENTITÉ STABLE, ET CE N'EST PAS UNE FORMALITÉ : ce rappel est lu par l'effet de relecture du
   * volet. Écrit en ligne, il changerait d'identité à chaque rendu — donc à chaque relecture —, l'effet se
   * rejouerait sans fin et le volet interrogerait le serveur en boucle. C'est la porte `lint` qui l'a dit.
   */
  const noterStatut = useCallback((id: string, status: string) => {
    // Même objet rendu quand rien n'a bougé : une relecture identique ne doit pas provoquer de rendu.
    setObserved((o) => (o[id] === status ? o : { ...o, [id]: status }));
  }, []);
  const narrow = useNarrow();

  // ⚠️ UNE LECTURE DU VOLET NE SURVIT PAS AU RECHARGEMENT DE LA LISTE : elle est plus fraîche que la photo
  //    QU'ELLE A SUIVIE, jamais que la suivante. Un rejeu change précisément le statut côté serveur — garder
  //    l'ancienne lecture ferait mentir le tableau dans l'autre sens, en forçant un statut périmé.
  const rechargerListe = async (): Promise<void> => { setObserved({}); await reload(); };

  const setQ = (v: string) => { const n = new URLSearchParams(sp); if (v) n.set('q', v); else n.delete('q'); setSp(n, { replace: true }); setPage(0); };

  /**
   * ⚠️ 🔴 LE FILTRE « À TRAITER » VIT DANS L'URL, COMME LA RECHERCHE — et c'est un CORRECTIF.
   *
   * La supervision renvoie ici par `/journal?erreurs=1`, sous un bouton qui annonce « Voir les N échecs ».
   * Ce paramètre n'était LU PAR PERSONNE : l'opérateur atterrissait sur le journal COMPLET pendant que le
   * bouton promettait une liste filtrée — un bouton qui fait semblant (§23.0), et le genre de promesse que
   * rien ne signale, puisque la navigation, elle, fonctionne.
   *
   * ⚠️ Dérivé de l'URL plutôt qu'initialisé depuis elle : il n'existe alors AUCUN second endroit où l'état
   * pourrait rester en arrière, et le filtre devient partageable d'un lien — ce que le bouton supposait déjà.
   */
  const errOnly = sp.get('erreurs') === '1';
  const setErrOnly = (v: boolean) => { const n = new URLSearchParams(sp); if (v) n.set('erreurs', '1'); else n.delete('erreurs'); setSp(n, { replace: true }); setPage(0); };
  // ⚠️ UN SEUL ÉCRITURE D'URL pour « tout effacer » : deux appels bâtis sur la MÊME photo de `sp` se
  //    défont l'un l'autre — le second réécrirait le paramètre que le premier venait de retirer.
  const effacerFiltres = () => {
    const n = new URLSearchParams(sp); n.delete('erreurs'); n.delete('q'); setSp(n, { replace: true });
    setDir('all'); setSup('all'); setSite('all'); setPage(0);
  };

  const supplierNames = useMemo(() => [...new Set(raw.map((m) => m.supplierName).filter(Boolean) as string[])].sort(), [raw]);
  const siteNames = useMemo(() => [...new Set(raw.map((m) => m.siteName).filter(Boolean) as string[])].sort(), [raw]);

  const rows = useMemo(() => {
    const ql = q.toLowerCase();
    // Le statut relu par le volet l'emporte sur celui de la photo — c'est le même message, lu plus tard.
    let r = raw.map((m) => { const vu = observed[m.id]; return toRow(vu ? { ...m, status: vu } : m); }).filter((m) => {
      if (errOnly && !['err', 'warn'].includes(m.kind)) return false;
      if (dir !== 'all' && m.dir !== dir) return false;
      if (sup !== 'all' && m.supplier !== sup) return false;
      if (site !== 'all' && m.site !== site) return false;
      if (ql && !`${m.supplier} ${m.site} ${m.type} ${m.gln} ${m.status}`.toLowerCase().includes(ql)) return false;
      return true;
    });
    r = [...r].sort((a, b) => {
      const av = sortKey === 'replays' ? a.replays : sortKey === 'time' ? a.iso : String((a as unknown as Record<string, unknown>)[sortKey] ?? '');
      const bv = sortKey === 'replays' ? b.replays : sortKey === 'time' ? b.iso : String((b as unknown as Record<string, unknown>)[sortKey] ?? '');
      const c = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? c : -c;
    });
    return r;
  }, [raw, observed, q, errOnly, dir, sup, site, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PER));
  const pg = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(pg * PER, pg * PER + PER);
  const selRow = rows.find((m) => m.id === sel) ?? null;

  const chips = [
    // ⚠️ LE LIBELLÉ NOMME LES TROIS, parce que le filtre en retient trois : il laisse passer `err` ET `warn`,
    //    donc aussi les mauvais magasins — annoncer deux statuts sur trois laisserait croire à un filtre plus
    //    étroit qu'il n'est, et c'est bien le seau « à traiter » de la supervision qui arrive par ce lien.
    errOnly && { label: 'Statut : erreur / non reconnu / mauvais magasin', clear: () => setErrOnly(false) },
    dir !== 'all' && { label: 'Sens : ' + (dir === 'in' ? 'entrant' : 'sortant'), clear: () => setDir('all') },
    sup !== 'all' && { label: 'Fournisseur : ' + sup, clear: () => setSup('all') },
    site !== 'all' && { label: 'Site : ' + site, clear: () => setSite('all') },
    q && { label: 'Recherche : ' + q, clear: () => setQ('') },
  ].filter(Boolean) as { label: string; clear: () => void }[];

  // ⚠️ LE TRI EST UN BOUTON DANS L'EN-TÊTE, et l'en-tête DIT son ordre (`aria-sort`) : un `<th>` cliquable ne s'atteignait
  //    pas au clavier, et une aide technique ne savait pas quelle colonne ordonnait le journal.
  const th = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => (
    <th aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={{ textAlign: align, padding: '8px 12px', fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap', userSelect: 'none' }}>
      <button type="button" onClick={() => { if (sortKey === key) setSortDir((x) => (x === 'asc' ? 'desc' : 'asc')); else { setSortKey(key); setSortDir('asc'); } }}
        style={{ padding: 0, border: 'none', background: 'none', font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', color: 'inherit', cursor: 'pointer', borderRadius: 'var(--r-xs)' }}>
        {label}{sortKey === key && <span aria-hidden style={{ marginLeft: 4, color: 'var(--teal-600)' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );

  const selCtl = { height: 32, borderRadius: 'var(--r-xs)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', padding: '0 9px' } as const;

  // ⚠️ TITRE DE PAGE AJOUTÉ (relevé de la maquette), et son sous-titre DIT CE QU'IL MONTRE. La maquette
  // y annonce une période ; nous n'en avons pas (la route lit les N plus récents, sans fenêtre), donc on
  // annonce ce qui est VRAI — le nombre chargé, et le plafond QUAND il est atteint. Promettre une
  // période que la requête ne borne pas serait pire que de n'en pas parler.
  const pageHead = (
    <PageHead title="Journal des échanges"
      sub={raw.length >= PLAFOND_LECTURE
        ? `${PLAFOND_LECTURE} messages les plus récents — la liste est plafonnée, affinez les filtres pour voir au-delà`
        : `${raw.length} message(s) · tout l'historique retenu`} />
  );

  return (
    // ⚠️ AUCUN `empty` ICI : l'état vide de la garde remplacerait les filtres et « Rafraîchir », les deux seuls gestes
    //    utiles quand rien n'est encore arrivé. Le vide se dit DANS la carte du tableau, comme la maquette.
    <DataGate state={state} onRetry={() => void rechargerListe()} error={error} head={pageHead}>

      {/* Barre de filtres */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ position: 'relative', minWidth: 220 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-400)', display: 'grid' }}><Icon name="search" size={14} /></span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" style={{ ...selCtl, paddingLeft: 32, width: 240 }} />
        </div>
        <select aria-label="Sens" value={dir} onChange={(e) => { setDir(e.target.value as typeof dir); setPage(0); }} style={selCtl}>
          <option value="all">Tous les sens</option><option value="in">Entrant</option><option value="out">Sortant</option>
        </select>
        <select aria-label="Fournisseur" value={sup} onChange={(e) => { setSup(e.target.value); setPage(0); }} style={selCtl}>
          <option value="all">Tous fournisseurs</option>{supplierNames.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select aria-label="Site" value={site} onChange={(e) => { setSite(e.target.value); setPage(0); }} style={selCtl}>
          <option value="all">Tous sites</option>{siteNames.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" aria-pressed={errOnly} onClick={() => setErrOnly(!errOnly)}
          style={{ ...selCtl, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, borderColor: errOnly ? 'var(--rose)' : 'var(--ink-200)', color: errOnly ? 'var(--rose-fg)' : 'var(--ink-700)', background: errOnly ? 'var(--rose-bg)' : 'var(--paper)' }}>
          <Icon name="alert" size={13} />À traiter seulement
        </button>
        <button type="button" onClick={() => void rechargerListe()} style={{ ...selCtl, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="refresh" size={13} />Rafraîchir
        </button>
      </div>

      {chips.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
          {chips.map((c, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 24, padding: '0 6px 0 10px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: 'var(--t-note)', fontWeight: 500 }}>
              {c.label}
              <button type="button" onClick={c.clear} className="btn-icon"><Icon name="x" size={12} /></button>
            </span>
          ))}
          <button type="button" onClick={effacerFiltres} style={{ background: 'transparent', border: 'none', color: 'var(--teal-700)', fontSize: 'var(--t-dense)', fontWeight: 600 }}>Tout effacer</button>
        </div>
      )}

      {/* Table + volet */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <Card style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 720 }}>
              <thead><tr style={{ borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
                {th('time', 'Horodatage')}{th('dir', 'Sens')}{th('supplier', 'Fournisseur')}{th('site', 'Site · GLN')}{th('type', 'Type')}{th('status', 'Statut')}{th('replays', 'Rejeux', 'right')}
              </tr></thead>
              <tbody>
                {pageRows.map((m) => (
                  <tr key={m.id} onClick={() => setSel(m.id)}
                    style={{ borderBottom: '1px solid var(--ink-100)', cursor: 'pointer', height: 'var(--row-h)', background: m.id === sel ? 'var(--ink-50)' : 'transparent', boxShadow: m.id === sel ? 'inset 2px 0 0 var(--teal-500)' : 'none' }}>
                    {/* ⚠️ L'HORODATAGE EST LE BOUTON DE LA LIGNE — le chemin du clavier ; le clic sur la ligne n'en est qu'un raccourci. */}
                    <td style={{ padding: '0 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-600)' }}>
                      <button type="button" aria-label={`Ouvrir le message de ${m.supplier} du ${m.time}`} aria-current={m.id === sel || undefined}
                        onClick={(e) => { e.stopPropagation(); setSel(m.id); }}
                        style={{ padding: 0, border: 'none', background: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', borderRadius: 'var(--r-xs)' }}>{m.time}</button>
                    </td>
                    <td style={{ padding: '0 12px' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--t-meta)', color: m.dir === 'in' ? 'var(--ink-700)' : 'var(--ink-500)' }}><Icon name={m.dir === 'in' ? 'inbound' : 'outbound'} size={12} sw={2.2} />{m.dir === 'in' ? 'entrant' : 'sortant'}</span></td>
                    <td style={{ padding: '0 12px', fontSize: 'var(--t-body)', fontWeight: 500, color: 'var(--ink-800)' }}>{m.supplier}</td>
                    <td style={{ padding: '0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-700)' }}>{m.site}<div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>{m.gln}</div></td>
                    <td style={{ padding: '0 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>{m.type}</td>
                    <td style={{ padding: '0 12px' }}><Badge kind={m.kind}>{m.status}</Badge></td>
                    <td style={{ padding: '0 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--t-body)', color: m.replays > 0 ? 'var(--ink-800)' : 'var(--ink-400)' }}>{m.replays}</td>
                  </tr>
                ))}
                {/* ⚠️ DEUX VIDES DIFFÉRENTS : rien n'est arrivé du tout (le serveur ne rend aucun message — aucun filtre
                    n'y est pour rien, la phrase ne le prétend pas), ou les filtres écartent tout ce qui est arrivé. */}
                {pageRows.length === 0 && raw.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: '56px 24px', textAlign: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-400)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}><Icon name="layers" size={22} /></div>
                    <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--ink-800)' }}>Aucun échange retenu</div>
                    <p style={{ margin: '7px auto 0', maxWidth: 430, fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
                      Rien n’a encore été récupéré ni déposé. Attendez le prochain créneau planifié — hors créneau, il peut ne rien y avoir.
                    </p>
                  </td></tr>
                )}
                {pageRows.length === 0 && raw.length > 0 && <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--ink-500)', fontSize: 'var(--t-lead)' }}>Aucun message pour ces filtres.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: '1px solid var(--ink-100)' }}>
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{rows.length} message{rows.length > 1 ? 's' : ''}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" disabled={pg === 0} onClick={() => setPage(pg - 1)} className="btn-icon" style={{ color: pg === 0 ? 'var(--ink-300)' : 'var(--ink-700)', cursor: pg === 0 ? 'not-allowed' : 'pointer' }}><Icon name="chevronR" size={14} style={{ transform: 'rotate(180deg)' }} /></button>
              <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{pg + 1} / {pageCount}</span>
              <button type="button" disabled={pg >= pageCount - 1} onClick={() => setPage(pg + 1)} className="btn-icon" style={{ color: pg >= pageCount - 1 ? 'var(--ink-300)' : 'var(--ink-700)', cursor: pg >= pageCount - 1 ? 'not-allowed' : 'pointer' }}><Icon name="chevronR" size={14} /></button>
            </div>
          </div>
        </Card>

        {selRow && (
          <MessagePanel row={selRow} overlay={narrow} onClose={() => setSel(null)}
            onReplay={async () => {
              if (!guard()) return;
              try {
                // Verdict RÉEL du rejeu (§23.0) : le toast dit le nouveau statut + pourquoi, puis on recharge.
                const r = await api.replayMessage(selRow.id);
                showToast(r.detail, !r.ok ? 'err' : r.status === 'translated' || r.status === 'received' ? 'ok' : 'warn');
                await rechargerListe();
              } catch (e) {
                showToast('Rejeu impossible : ' + (e as Error).message, 'err');
              }
            }}
            onDownload={() => {
              // Lien direct : le navigateur télécharge (Content-Disposition), cookie de session envoyé.
              const a = document.createElement('a');
              a.href = rawDownloadUrl(selRow.id);
              a.download = '';
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
            onCopy={() => { void navigator.clipboard?.writeText(selRow.id); showToast('Identifiant copié', 'ok'); }}
            onStatus={noterStatut} />
        )}
      </div>
    </DataGate>
  );
}

// Volet de détail — charge le message RÉEL (canonique + erreur + événements). Plus aucun aperçu factice (§23.0).
function MessagePanel({ row, overlay, onClose, onReplay, onDownload, onCopy, onStatus }: {
  row: Row; overlay: boolean; onClose: () => void; onReplay: () => void; onDownload: () => void; onCopy: () => void;
  /** Statut relu par le volet, reporté à la LISTE : sans lui le tableau contredirait le volet (cf. `observed`). */
  onStatus: (id: string, status: string) => void;
}) {
  const [detail, setDetail] = useState<ApiMessageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<'canon' | 'events'>('canon');
  // ⚠️ EN ÉCRAN ÉTROIT LE VOLET EST UNE MODALE : Échap le ferme, et le focus y entre puis revient au déclencheur — au seul
  //    pointeur, il ne se quittait pas au clavier. À côté de la liste, il n'est pas superposé et le socle ne fait rien.
  const { panelRef } = useOverlayPanel({ open: overlay, onClose });
  const titleId = useId();

  useEffect(() => {
    let alive = true;
    let minuterie: ReturnType<typeof setTimeout> | undefined;
    setLoading(true); setFailed(false); setDetail(null); setTab('canon');

    const lire = (): void => {
      api.get<ApiMessageDetail>(`/admin/messages/${row.id}`)
        .then((d) => {
          if (!alive) return;
          setDetail(d);
          setLoading(false);
          onStatus(row.id, d.message.status);
          // ⚠️ AUCUNE CADENCE DE FILE N'EST DEVINÉE ICI : on relit toutes les 6 s, ce qui fait voir la
          //    bascule dans la seconde où elle arrive, quel que soit le rythme du dépôt — et ça s'arrête
          //    de soi-même à l'aboutissement (`isInTransit` porte la règle, cf. `message-progress.ts`).
          if (isInTransit(d.message.status)) minuterie = setTimeout(lire, 6_000);
        })
        .catch(() => { if (alive) { setFailed(true); setLoading(false); } });
    };
    lire();

    return () => { alive = false; if (minuterie) clearTimeout(minuterie); };
  }, [row.id, onStatus]);

  /**
   * LE STATUT AFFICHÉ EST CELUI DU DERNIER RELEVÉ DU VOLET, pas celui de la ligne de liste.
   *
   * ⚠️ 🔴 SANS CELA LA RELECTURE NE SERVAIT À RIEN : le volet relisait bien le message toutes les six
   * secondes, et continuait d'afficher « Traduit » — parce que la progression, la conclusion et le bouton
   * de rejeu étaient TOUS dérivés de `row`, figé au dernier chargement de la LISTE, qui n'est rechargée
   * que par une action humaine. Une relecture dont le résultat n'atteint pas l'écran ne corrige rien, et
   * le symptôme est exactement celui qu'elle devait faire disparaître (§23.0).
   * ⚠️ `row` reste le repli : tant que le détail n'est pas arrivé, la ligne est ce qu'on sait de plus frais.
   */
  const statusRaw = detail?.message.status ?? row.statusRaw;
  const replays = detail?.message.replayCount ?? row.replays;

  // Progression 4 étapes : étapes atteintes = ok, le reste = à venir. L'erreur RÉELLE est portée par la conclusion.
  const labels = row.dir === 'in' ? ['Reçu', 'Traduit', 'Poussé au BO', 'Confirmé'] : ['Relu du back-office', 'Traduit fournisseur', 'Déposé', 'Confirmé'];
  const reached = reachedStep(statusRaw);
  const isMis = statusRaw === 'misrouted';
  const isDup = statusRaw === 'duplicate';
  const isErr = statusRaw === 'error' || statusRaw === 'unrecognized' || isMis;
  // ⚠️ CE QUE LE REJEU PEUT ET NE PEUT PAS, DIT AVANT LE CLIC : le bouton est désormais offert sur un
  //    « mauvais magasin », et sans cette phrase il laisserait croire qu'il répare le fichier. Il ne répare
  //    que ce qui est de NOTRE côté ; un fichier réellement destiné ailleurs revient dans le même état.
  const suiteMis = " Le rejeu n'aboutit que si la correspondance a été corrigée de notre côté (lien magasin, GLN du mapping) — sinon le message revient dans cet état.";
  const conclusion = isErr
    ? { tone: 'var(--rose-fg)', bg: 'var(--rose-bg)', text: (detail?.message.errorDetail ?? (statusRaw === 'unrecognized' ? 'Format entrant sans mapping — conservé, à mapper puis rejouer.' : isMis ? 'Fichier destiné à un autre magasin que son dossier — non livré (§5.1).' : 'En erreur — voir le détail.')) + (isMis ? suiteMis : '') }
    : isDup ? { tone: 'var(--ink-600)', bg: 'var(--ink-50)', text: detail?.message.errorDetail ?? 'Déjà intégré : ce document a déjà été reçu et intégré — non réintégré (§7bis).' }
    : statusRaw === 'confirmed' ? { tone: 'var(--ok-fg)', bg: 'var(--ok-bg)', text: 'Rien à faire — le message a suivi son cours normalement.' }
      // ⚠️ 🔴 UN ÉTAT EN TRANSIT DOIT DIRE CE QU'IL ATTEND, sinon une étape grise se lit comme un blocage.
      //    « En cours de traitement » laissait croire qu'un message `translated` était coincé, alors que
      //    l'envoi au back-office est traité par un WORKER : la réponse au rejeu ne peut pas le porter, et
      //    aucune seconde réponse n'atteint le navigateur — d'où la relecture, et la phrase qui l'annonce.
      // ⚠️ AUCUNE DURÉE ÉCRITE ICI : ni la cadence de la file ni celle du filet ne sont des réglages que cet
      //    écran lit. Annoncer « 5 minutes » serait vrai un temps puis faux, et personne ne relit une phrase
      //    d'écran. On dit le MÉCANISME, qui ne dérive pas.
      : statusRaw === 'translated'
        ? { tone: 'var(--ink-600)', bg: 'var(--ink-50)', text: "Traduit. L'envoi au back-office est en file — cet écran se met à jour tout seul, inutile de recharger." }
        : statusRaw === 'pushed'
          ? { tone: 'var(--ink-600)', bg: 'var(--ink-50)', text: 'Déposé au back-office, en attente de sa confirmation.' }
          : { tone: 'var(--ink-600)', bg: 'var(--ink-50)', text: 'Reçu, en attente de traduction au prochain passage.' };
  /**
   * Rejouable — LE SERVEUR TRANCHE, L'ÉCRAN AFFICHE.
   *
   * ⚠️ 🔴 CET ÉCRAN A TENU SA PROPRE LISTE (`error`/`unrecognized`), et c'est exactement ainsi qu'une règle
   * se met à mentir : le jour où `misrouted` est devenu rejouable côté serveur, le bouton est resté caché —
   * l'opérateur n'avait plus aucun geste sur un fichier que NOUS pouvions corriger, et rien ne le signalait.
   * Le verdict vient donc avec le message (`replayable`), et aucune énumération de statuts ne vit ici.
   * ⚠️ Pas de bouton avant que le détail soit arrivé : sans verdict, on ne devine pas.
   */
  const canReplay = detail?.message.replayable ?? false;
  const meta: [string, string][] = [
    ['Fournisseur', row.supplier], ['Transport', row.transport], ['Site · GLN', `${row.site} · ${row.gln}`],
    ['Rejeux', String(replays)], ['Type canonique', row.type],
    ['Rétention', fmtDate(detail?.message.retentionUntil)],
  ];

  const body = (
    <div style={{ width: overlay ? '100%' : 452, flexShrink: 0, background: 'var(--paper)', borderLeft: overlay ? 'none' : '1px solid var(--ink-200)', borderRadius: overlay ? 0 : 14, boxShadow: overlay ? 'none' : 'var(--sh-sm)', animation: 'slidein .18s', display: 'flex', flexDirection: 'column', maxHeight: overlay ? '100%' : 'calc(100vh - 160px)', overflowY: 'auto' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ink-100)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Pastille dérivée du statut RELU (`statusRaw`), jamais des libellés figés de la ligne. */}
          <Badge kind={statusKind(statusRaw)}>{statusLabel(statusRaw)}</Badge>
          <div id={titleId} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: 'var(--ink-800)', marginTop: 8 }}>{row.type} · {row.time}</div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 3 }}>{row.supplier} <Icon name="arrowR" size={11} style={{ verticalAlign: 'middle', color: 'var(--ink-300)' }} /> {row.site}</div>
        </div>
        <button type="button" onClick={onClose} className="btn-icon"><Icon name="x" size={16} /></button>
      </div>

      {/* Progression */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ink-100)' }}>
        {labels.map((label, i) => {
          const state = i < reached ? 'ok' : 'todo';
          const col = state === 'ok' ? 'var(--ok)' : 'var(--ink-200)';
          return (
            <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', paddingBottom: i < labels.length - 1 ? 12 : 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: col, display: 'grid', placeItems: 'center', color: '#fff' }}>{state === 'ok' && <Icon name="check" size={11} sw={2.6} />}</div>
                {i < labels.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 14, background: 'var(--ink-100)' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingBottom: 2 }}>
                <div style={{ fontSize: 'var(--t-body)', fontWeight: 500, color: state === 'todo' ? 'var(--ink-400)' : 'var(--ink-800)' }}>{label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Métadonnées */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ink-100)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
        {meta.map(([k, v]) => (
          <div key={k}><div style={{ fontSize: 'var(--t-micro)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>{k}</div><div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-800)', marginTop: 2 }}>{v}</div></div>
        ))}
      </div>

      {/* Onglets canonique / journal (RÉELS) */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink-100)' }}>
        <div style={{ display: 'inline-flex', background: 'var(--ink-100)', borderRadius: 'var(--r-xs)', padding: 3, gap: 3, marginBottom: 10 }}>
          {(['canon', 'events'] as const).map((tk) => (
            <button aria-pressed={tab === tk} key={tk} type="button" onClick={() => setTab(tk)} style={{ height: 24, padding: '0 12px', border: 'none', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-note)', fontWeight: 600, background: tab === tk ? 'var(--paper)' : 'transparent', color: tab === tk ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: tab === tk ? 'var(--sh-sm)' : 'none' }}>{tk === 'canon' ? 'Canonique' : 'Journal'}</button>
          ))}
        </div>
        {loading && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', padding: '8px 0' }}>Chargement du détail…</div>}
        {failed && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', padding: '8px 0' }}>Détail indisponible.</div>}
        {detail && tab === 'canon' && (
          <pre style={{ margin: 0, padding: 12, background: 'var(--ink-50)', border: '1px solid var(--ink-100)', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontFamily: 'var(--font-mono)', color: 'var(--ink-700)', overflowX: 'auto', maxHeight: 220 }}>
{detail.message.canonical ? JSON.stringify(detail.message.canonical, null, 2) : '— pas encore de canonique (message non traduit)'}
          </pre>
        )}
        {detail && tab === 'events' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {detail.events.length === 0 && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>Aucun événement.</div>}
            {detail.events.map((e) => (
              <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 'var(--t-note)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-400)' }}>{hhmmss(e.createdAt)}</span>
                <span style={{ fontWeight: 600, color: e.level === 'error' ? 'var(--rose-fg)' : e.level === 'warn' ? 'var(--amber-fg)' : 'var(--ink-700)' }}>{e.kind}</span>
                <span style={{ color: 'var(--ink-500)', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{typeof e.detail === 'object' && e.detail ? JSON.stringify(e.detail) : String(e.detail ?? '')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Conclusion + actions */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ padding: '10px 12px', borderRadius: 'var(--r-sm)', background: conclusion.bg, color: conclusion.tone, fontSize: 'var(--t-dense)', lineHeight: 1.4, marginBottom: 12 }}>{conclusion.text}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canReplay && <Btn icon="refresh" size="sm" onClick={onReplay}>Rejouer</Btn>}
          {/* Brut proposé UNIQUEMENT s'il existe (un sortant ou un brut purgé n'en a pas) — pas de bouton mort. */}
          {detail?.message.rawRef && <Btn icon="download" size="sm" variant="ghost" mutating={false} onClick={onDownload}>Télécharger le brut</Btn>}
          <Btn icon="copy" size="sm" variant="ghost" mutating={false} onClick={onCopy}>Copier l'id</Btn>
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 10 }}>{row.id}</div>
      </div>
    </div>
  );

  if (overlay) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(12,14,16,.45)', display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
        <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
          style={{ width: 'min(452px, 100%)', height: '100%' }} onClick={(e) => e.stopPropagation()}>{body}</div>
      </div>
    );
  }
  return body;
}
